/* eslint-disable no-unused-vars */
import React, { useState, useCallback } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  TextInput,
  Image,
  Alert,
  ScrollView,
  StatusBar,
  ActivityIndicator,
  RefreshControl,
  Linking,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  MaterialIcons,
} from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  companyProfile: `${API_BASE_URL}/api/data/company_profiles`,
  vendors: `${API_BASE_URL}/api/data/vendors`,
  orders: `${API_BASE_URL}/api/data/orders`,
};

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
  },
});

api.interceptors.request.use(
  async (config) => {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        config.params = { ...config.params, company_id: parseInt(companyId) };
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const SuppliersVendorListScreen = ({ navigation }) => {
  const [activeTab, setActiveTab] = useState('All Suppliers');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Materials');
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [sortBy, setSortBy] = useState('name');
  const [filters, setFilters] = useState({ status: 'All', rating: 'All' });
  const [showFilters, setShowFilters] = useState(false);

  const categories = [
    'All Materials',
    'Concrete & Cement',
    'Steel & Metals',
    'Timber & Wood',
    'Glass & Windows',
    'Electrical',
    'Plumbing',
    'Insulation',
    'Roofing',
    'Flooring',
  ];

  useFocusEffect(
    useCallback(() => {
      loadCompanyLogo();
      fetchSuppliers();
    }, [])
  );

  const loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        setCompanyLogo(profile.logo || null);
      }
      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        const response = await api.get(API_ENDPOINTS.companyProfile, {
          params: { company_id: companyId },
        });
        if (response.data?.success && response.data.data?.length > 0) {
          const latestProfile = response.data.data[0];
          setCompanyLogo(latestProfile.logo || null);
          await AsyncStorage.setItem('company_profile', JSON.stringify(latestProfile));
        }
      }
    } catch (error) {
      console.warn('Error loading company logo:', error);
    }
  };

  // Transform database vendor object to UI shape
  const transformVendor = (v) => ({
    id: v.id,
    name: v.company_name,
    materials: v.materials || [],
    ratings: v.ratings || { quality: 0, reliability: 0, cost: 0, time: 0 },
    overall: Number(v.overall) || 0,
    contact: {
      phone: v.phone,
      email: v.email,
      person: v.contact_person,
      website: v.website,
    },
    location: v.city && v.country ? `${v.city}, ${v.country}` : (v.address || 'Unknown'),
    status: v.status,
    companySize: v.company_size,
    yearsInBusiness: v.years_in_business,
    notes: v.notes,
    isFavorite: v.is_favorite || false,
    lastUsed: v.last_used,
    projectsCompleted: Number(v.projects_completed) || 0,
    onTimeRate: Number(v.on_time_rate) || 100,
  });

  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      const companyId = await AsyncStorage.getItem('company_id');
      const response = await api.get(API_ENDPOINTS.vendors, {
        params: { company_id: companyId, type: 'supplier' },
      });
      let data = [];
      if (response.data?.success && Array.isArray(response.data.data)) {
        data = response.data.data;
      } else if (Array.isArray(response.data)) {
        data = response.data;
      }
      const transformed = data.map(transformVendor);
      setSuppliers(transformed);
      applyFilters(transformed);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      Alert.alert('Error', 'Failed to load suppliers');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const applyFilters = (data = suppliers) => {
    let filtered = [...data];

    // Tab filter
    if (activeTab === 'Favorites') {
      filtered = filtered.filter((s) => s.isFavorite);
    } else if (activeTab === 'Recently Used') {
      filtered = filtered.sort((a, b) => {
        if (!a.lastUsed) return 1;
        if (!b.lastUsed) return -1;
        return new Date(b.lastUsed) - new Date(a.lastUsed);
      });
    }

    // Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          s.name?.toLowerCase().includes(q) ||
          s.materials?.some((m) => m.toLowerCase().includes(q)) ||
          s.location?.toLowerCase().includes(q)
      );
    }

    // Category filter
    if (selectedCategory !== 'All Materials') {
      filtered = filtered.filter((s) =>
        s.materials?.some((m) => m.toLowerCase().includes(selectedCategory.toLowerCase()))
      );
    }

    // Status filter
    if (filters.status !== 'All') {
      filtered = filtered.filter((s) => s.status === filters.status);
    }

    // Rating filter
    if (filters.rating !== 'All') {
      const min = parseFloat(filters.rating.replace('+', ''));
      filtered = filtered.filter((s) => s.overall >= min);
    }

    // Sort
    filtered.sort((a, b) => {
      if (sortBy === 'rating') return b.overall - a.overall;
      if (sortBy === 'lastUsed') {
        if (!a.lastUsed) return 1;
        if (!b.lastUsed) return -1;
        return new Date(b.lastUsed) - new Date(a.lastUsed);
      }
      return a.name?.localeCompare(b.name);
    });

    setFilteredSuppliers(filtered);
  };

  React.useEffect(() => {
    applyFilters();
  }, [searchQuery, selectedCategory, activeTab, filters, sortBy, suppliers]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchSuppliers();
  };

  // ✅ FIXED: toggleFavorite using correct endpoint and snake_case field
  const toggleFavorite = async (id, currentValue) => {
    try {
      await api.put(`${API_ENDPOINTS.vendors}/${id}`, { is_favorite: !currentValue });
      fetchSuppliers();
      Alert.alert('Success', 'Favorite updated');
    } catch (error) {
      console.error('Toggle favorite error:', error);
      Alert.alert('Error', 'Could not update favorite');
    }
  };

  const handleAddSupplier = () => {
    navigation.navigate('AddSupplier');
  };

  const handleEditSupplier = (supplier) => {
    navigation.navigate('EditSupplier', { supplier });
  };

  // ✅ Real phone call
  const handleCall = (phone) => {
    if (!phone) {
      Alert.alert('No phone number', 'This supplier has no phone number on file.');
      return;
    }
    Alert.alert('Call', `Call ${phone}?`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Call',
        onPress: () => {
          Linking.openURL(`tel:${phone}`).catch(() =>
            Alert.alert('Error', 'Could not initiate call.')
          );
        },
      },
    ]);
  };

  // ✅ Navigate to EmailManagement with pre-filled data
  const handleEmail = (email, supplierName) => {
    if (!email) {
      Alert.alert('No email', 'This supplier has no email address on file.');
      return;
    }
    navigation.navigate('EmailManagementScreen', {
      mode: 'compose',
      client: {
        email,
        name: supplierName,
      },
    });
  };

  const handlePlaceOrder = async (supplier) => {
    Alert.alert(
      'Place Order',
      `Create a new order with ${supplier.name}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Create Order',
          onPress: async () => {
            setLoading(true);
            try {
              const companyId = await AsyncStorage.getItem('company_id');
              const orderPayload = {
                company_id: parseInt(companyId),
                supplier_id: supplier.id,
                order_date: new Date().toISOString().split('T')[0],
                status: 'pending',
                total_amount: 0,
                notes: `Order from ${supplier.name}`,
              };
              const response = await api.post(API_ENDPOINTS.orders, orderPayload);
              if (response.data?.success) {
                Alert.alert('Success', 'Order placed successfully!');
              } else {
                Alert.alert('Error', 'Failed to place order.');
              }
            } catch (error) {
              console.error('Error placing order:', error);
              Alert.alert('Error', 'Could not place order. Please try again.');
            } finally {
              setLoading(false);
            }
          },
        },
      ]
    );
  };

  const handleGoBack = () => {
    if (navigation.canGoBack()) navigation.goBack();
  };

  const renderRatingBar = (value) => {
    const percentage = (value / 5) * 100;
    return (
      <View style={styles.ratingBarContainer}>
        <View style={[styles.ratingBar, { width: `${percentage}%` }]} />
      </View>
    );
  };

  const renderSupplierCard = ({ item }) => (
    <TouchableOpacity
      style={[styles.supplierCard, item.status === 'Inactive' && styles.inactiveCard]}
      onPress={() => setSelectedSupplier(item)}
    >
      <View style={styles.supplierHeader}>
        <Text style={styles.supplierName}>{item.name}</Text>
        <View style={[styles.statusBadge, item.status === 'Active' ? styles.activeStatus : styles.inactiveStatus]}>
          <Text style={styles.statusText}>{item.status}</Text>
        </View>
      </View>
      <Text style={styles.supplierMaterials}>{(item.materials || []).join(', ')}</Text>
      <Text style={styles.supplierLocation}>
        <Ionicons name="location-outline" size={14} color="#64748B" /> {item.location}
      </Text>

      <View style={styles.ratingsContainer}>
        <View style={styles.overallRating}>
          <Text style={styles.overallRatingValue}>{item.overall?.toFixed(1) || '0.0'}</Text>
          <Text style={styles.overallRatingLabel}>Overall</Text>
        </View>
        <View style={styles.detailedRatings}>
          <View style={styles.ratingItem}>
            <Text style={styles.ratingLabel}>Quality</Text>
            {renderRatingBar(item.ratings?.quality || 0)}
            <Text style={styles.ratingValue}>{(item.ratings?.quality || 0).toFixed(1)}</Text>
          </View>
          <View style={styles.ratingItem}>
            <Text style={styles.ratingLabel}>Reliability</Text>
            {renderRatingBar(item.ratings?.reliability || 0)}
            <Text style={styles.ratingValue}>{(item.ratings?.reliability || 0).toFixed(1)}</Text>
          </View>
          <View style={styles.ratingItem}>
            <Text style={styles.ratingLabel}>Cost</Text>
            {renderRatingBar(item.ratings?.cost || 0)}
            <Text style={styles.ratingValue}>{(item.ratings?.cost || 0).toFixed(1)}</Text>
          </View>
          <View style={styles.ratingItem}>
            <Text style={styles.ratingLabel}>Time</Text>
            {renderRatingBar(item.ratings?.time || 0)}
            <Text style={styles.ratingValue}>{(item.ratings?.time || 0).toFixed(1)}</Text>
          </View>
        </View>
      </View>

      <View style={styles.supplierActions}>
        <TouchableOpacity style={styles.actionButton} onPress={() => handleCall(item.contact?.phone)}>
          <Ionicons name="call-outline" size={16} color="#fff" />
          <Text style={styles.actionButtonText}>Call</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.actionButton} onPress={() => handleEmail(item.contact?.email, item.name)}>
          <Ionicons name="mail-outline" size={16} color="#fff" />
          <Text style={styles.actionButtonText}>Email</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[styles.actionButton, styles.primaryActionButton]} onPress={() => handlePlaceOrder(item)}>
          <MaterialCommunityIcons name="file-document-outline" size={16} color="#fff" />
          <Text style={styles.actionButtonText}>Order</Text>
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );

  const renderFilterModal = () => {
    if (!showFilters) return null;
    return (
      <View style={styles.filterOverlay}>
        <View style={styles.filterModal}>
          <View style={styles.filterHeader}>
            <Text style={styles.filterTitle}>Filter & Sort</Text>
            <TouchableOpacity onPress={() => setShowFilters(false)}>
              <Ionicons name="close" size={24} color="#0f172a" />
            </TouchableOpacity>
          </View>
          <View style={styles.filterContent}>
            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Sort By</Text>
              <View style={styles.filterOptions}>
                {[
                  { key: 'name', label: 'Name' },
                  { key: 'rating', label: 'Rating' },
                  { key: 'lastUsed', label: 'Recently Used' },
                ].map((opt) => (
                  <TouchableOpacity
                    key={opt.key}
                    style={[styles.filterOption, sortBy === opt.key && styles.activeFilterOption]}
                    onPress={() => setSortBy(opt.key)}
                  >
                    <Text style={[styles.filterOptionText, sortBy === opt.key && styles.activeFilterOptionText]}>
                      {opt.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Status</Text>
              <View style={styles.filterOptions}>
                {['All', 'Active', 'Inactive'].map((s) => (
                  <TouchableOpacity
                    key={s}
                    style={[styles.filterOption, filters.status === s && styles.activeFilterOption]}
                    onPress={() => setFilters((prev) => ({ ...prev, status: s }))}
                  >
                    <Text style={[styles.filterOptionText, filters.status === s && styles.activeFilterOptionText]}>
                      {s}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Minimum Rating</Text>
              <View style={styles.filterOptions}>
                {['All', '4.5+', '4.0+', '3.5+'].map((r) => (
                  <TouchableOpacity
                    key={r}
                    style={[styles.filterOption, filters.rating === r && styles.activeFilterOption]}
                    onPress={() => setFilters((prev) => ({ ...prev, rating: r }))}
                  >
                    <Text style={[styles.filterOptionText, filters.rating === r && styles.activeFilterOptionText]}>
                      {r}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </View>
          <View style={styles.filterActions}>
            <TouchableOpacity
              style={styles.clearFiltersButton}
              onPress={() => {
                setFilters({ status: 'All', rating: 'All' });
                setSortBy('name');
                setShowFilters(false);
              }}
            >
              <Text style={styles.clearFiltersText}>Clear All</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.applyFiltersButton} onPress={() => setShowFilters(false)}>
              <Text style={styles.applyFiltersText}>Apply</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  };

  const renderDetailView = () => {
    if (!selectedSupplier) return null;
    return (
      <View style={styles.detailOverlay}>
        <View style={styles.detailContainer}>
          <View style={styles.detailHeader}>
            <Text style={styles.detailTitle}>{selectedSupplier.name}</Text>
            <TouchableOpacity onPress={() => setSelectedSupplier(null)}>
              <Ionicons name="close" size={24} color="#0f172a" />
            </TouchableOpacity>
          </View>
          <ScrollView style={styles.detailContent}>
            <View style={styles.detailSection}>
              <Text style={styles.sectionTitle}>Contact Information</Text>
              <TouchableOpacity style={styles.contactItem} onPress={() => handleCall(selectedSupplier.contact?.phone)}>
                <Ionicons name="call-outline" size={18} color="#0e4e96" />
                <Text style={styles.contactText}>{selectedSupplier.contact?.phone || 'N/A'}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.contactItem} onPress={() => handleEmail(selectedSupplier.contact?.email, selectedSupplier.name)}>
                <Ionicons name="mail-outline" size={18} color="#0e4e96" />
                <Text style={styles.contactText}>{selectedSupplier.contact?.email || 'N/A'}</Text>
              </TouchableOpacity>
              <View style={styles.contactItem}>
                <Ionicons name="location-outline" size={18} color="#0e4e96" />
                <Text style={styles.contactText}>{selectedSupplier.location}</Text>
              </View>
              {selectedSupplier.contact?.website && (
                <View style={styles.contactItem}>
                  <Ionicons name="globe-outline" size={18} color="#0e4e96" />
                  <Text style={styles.contactText}>{selectedSupplier.contact.website}</Text>
                </View>
              )}
            </View>

            <View style={styles.detailSection}>
              <Text style={styles.sectionTitle}>Materials Supplied</Text>
              <View style={styles.materialTags}>
                {(selectedSupplier.materials || []).map((mat, idx) => (
                  <View key={idx} style={styles.materialTag}>
                    <Text style={styles.materialTagText}>{mat}</Text>
                  </View>
                ))}
              </View>
            </View>

            <View style={styles.detailSection}>
              <Text style={styles.sectionTitle}>Ratings</Text>
              <View style={styles.detailRatingsContainer}>
                <View style={styles.detailOverallRating}>
                  <Text style={styles.detailOverallValue}>{selectedSupplier.overall?.toFixed(1) || '0.0'}</Text>
                  <Text style={styles.detailOverallLabel}>Overall</Text>
                </View>
                <View style={styles.detailRatingsList}>
                  <View style={styles.detailRatingItem}>
                    <Text style={styles.detailRatingLabel}>Quality</Text>
                    <View style={styles.detailRatingBar}>
                      {renderRatingBar(selectedSupplier.ratings?.quality || 0)}
                    </View>
                    <Text style={styles.detailRatingValue}>{(selectedSupplier.ratings?.quality || 0).toFixed(1)}</Text>
                  </View>
                  <View style={styles.detailRatingItem}>
                    <Text style={styles.detailRatingLabel}>Reliability</Text>
                    <View style={styles.detailRatingBar}>
                      {renderRatingBar(selectedSupplier.ratings?.reliability || 0)}
                    </View>
                    <Text style={styles.detailRatingValue}>
                      {(selectedSupplier.ratings?.reliability || 0).toFixed(1)}
                    </Text>
                  </View>
                  <View style={styles.detailRatingItem}>
                    <Text style={styles.detailRatingLabel}>Cost</Text>
                    <View style={styles.detailRatingBar}>
                      {renderRatingBar(selectedSupplier.ratings?.cost || 0)}
                    </View>
                    <Text style={styles.detailRatingValue}>{(selectedSupplier.ratings?.cost || 0).toFixed(1)}</Text>
                  </View>
                  <View style={styles.detailRatingItem}>
                    <Text style={styles.detailRatingLabel}>Time</Text>
                    <View style={styles.detailRatingBar}>
                      {renderRatingBar(selectedSupplier.ratings?.time || 0)}
                    </View>
                    <Text style={styles.detailRatingValue}>{(selectedSupplier.ratings?.time || 0).toFixed(1)}</Text>
                  </View>
                </View>
              </View>
            </View>

            <View style={styles.detailSection}>
              <Text style={styles.sectionTitle}>Company Information</Text>
              {selectedSupplier.companySize && (
                <View style={styles.infoItem}>
                  <Text style={styles.infoLabel}>Company Size:</Text>
                  <Text style={styles.infoValue}>{selectedSupplier.companySize}</Text>
                </View>
              )}
              {selectedSupplier.yearsInBusiness && (
                <View style={styles.infoItem}>
                  <Text style={styles.infoLabel}>Years in Business:</Text>
                  <Text style={styles.infoValue}>{selectedSupplier.yearsInBusiness}</Text>
                </View>
              )}
              <View style={styles.infoItem}>
                <Text style={styles.infoLabel}>Status:</Text>
                <Text
                  style={[
                    styles.infoValue,
                    selectedSupplier.status === 'Active' ? styles.activeText : styles.inactiveText,
                  ]}
                >
                  {selectedSupplier.status}
                </Text>
              </View>
            </View>

            {selectedSupplier.notes && (
              <View style={styles.detailSection}>
                <Text style={styles.sectionTitle}>Notes</Text>
                <Text style={styles.notesText}>{selectedSupplier.notes}</Text>
              </View>
            )}

            <View style={styles.detailActions}>
              <TouchableOpacity style={styles.detailActionButton} onPress={() => handlePlaceOrder(selectedSupplier)}>
                <Text style={styles.detailActionText}>Place Order</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.detailActionButton, styles.secondaryButton]}
                onPress={() => handleEditSupplier(selectedSupplier)}
              >
                <Text style={styles.secondaryButtonText}>Edit Supplier</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </View>
      </View>
    );
  };

  const renderHeader = () => (
    <View style={styles.header}>
      <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
        <Ionicons name="chevron-back" size={24} color="#fff" />
      </TouchableOpacity>
      <View style={styles.headerCenter}>
        {companyLogo ? (
          <Image source={{ uri: companyLogo || undefined }} style={styles.headerLogo} />
        ) : (
          <MaterialCommunityIcons name="office-building" size={30} color="#fff" />
        )}
        <Text style={styles.headerTitle}>Suppliers</Text>
      </View>
      <View style={{ flexDirection: 'row', gap: 10 }}>
        <TouchableOpacity style={styles.headerButton} onPress={() => navigation.navigate('OrdersList')}>
          <Ionicons name="list" size={24} color="#fff" />
        </TouchableOpacity>
        <TouchableOpacity style={styles.headerButton}>
          <Ionicons name="help-circle-outline" size={24} color="#fff" />
        </TouchableOpacity>
      </View>
    </View>
  );

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
      {renderHeader()}

      <View style={styles.tabContainer}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {['All Suppliers', 'Favorites', 'Recently Used'].map((tab) => (
            <TouchableOpacity
              key={tab}
              style={[styles.tabButton, activeTab === tab && styles.activeTabButton]}
              onPress={() => setActiveTab(tab)}
            >
              <Text style={[styles.tabText, activeTab === tab && styles.activeTabText]}>{tab}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      <View style={styles.searchContainer}>
        <View style={styles.searchBar}>
          <Ionicons name="search" size={20} color="#64748B" />
          <TextInput
            style={styles.searchInput}
            placeholder="Search suppliers..."
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholderTextColor="#94A3B8"
          />
          {searchQuery ? (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <Ionicons name="close-circle" size={20} color="#64748B" />
            </TouchableOpacity>
          ) : null}
        </View>
        <TouchableOpacity style={styles.filterButton} onPress={() => setShowFilters(true)}>
          <Ionicons name="filter" size={20} color="#fff" />
        </TouchableOpacity>
      </View>

      <View style={styles.categoriesContainer}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {categories.map((cat) => (
            <TouchableOpacity
              key={cat}
              style={[styles.categoryButton, selectedCategory === cat && styles.activeCategoryButton]}
              onPress={() => setSelectedCategory(cat)}
            >
              <Text style={[styles.categoryText, selectedCategory === cat && styles.activeCategoryText]}>{cat}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      <View style={styles.summaryContainer}>
        <View style={styles.summaryItem}>
          <Text style={styles.summaryValue}>{filteredSuppliers.length}</Text>
          <Text style={styles.summaryLabel}>Suppliers</Text>
        </View>
        <View style={styles.summaryItem}>
          <Text style={styles.summaryValue}>
            {filteredSuppliers.filter((s) => s.status === 'Active').length}
          </Text>
          <Text style={styles.summaryLabel}>Active</Text>
        </View>
        <View style={styles.summaryItem}>
          <Text style={styles.summaryValue}>{filteredSuppliers.filter((s) => s.isFavorite).length}</Text>
          <Text style={styles.summaryLabel}>Favorites</Text>
        </View>
      </View>

      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading suppliers...</Text>
        </View>
      ) : (
        <FlatList
          data={filteredSuppliers}
          renderItem={renderSupplierCard}
          keyExtractor={(item) => item.id.toString()}
          contentContainerStyle={styles.supplierList}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <MaterialIcons name="search-off" size={48} color="#CBD5E1" />
              <Text style={styles.emptyTitle}>No suppliers found</Text>
              <Text style={styles.emptyText}>Try adjusting your search or filters</Text>
              <TouchableOpacity style={styles.addButtonEmptyState} onPress={handleAddSupplier}>
                <Text style={styles.addButtonText}>Add New Supplier</Text>
              </TouchableOpacity>
            </View>
          }
        />
      )}

      <TouchableOpacity style={styles.floatingButton} onPress={handleAddSupplier}>
        <Ionicons name="add" size={24} color="#fff" />
      </TouchableOpacity>

      {selectedSupplier && renderDetailView()}
      {renderFilterModal()}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  // ... (styles unchanged – they are the same as in previous versions, included for completeness)
  safeArea: { flex: 1, backgroundColor: '#4A90E2' },
  header: {
    backgroundColor: '#4A90E2',
    paddingVertical: 15,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: { padding: 5 },
  headerCenter: { flexDirection: 'row', alignItems: 'center', flex: 1 },
  headerLogo: { width: 30, height: 30, borderRadius: 15, resizeMode: 'cover', marginRight: 10 },
  headerTitle: { color: '#fff', fontSize: 20, fontWeight: 'bold', marginLeft: 10 },
  headerButton: { padding: 5 },
  tabContainer: {
    backgroundColor: '#fff',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  tabButton: {
    paddingHorizontal: 15,
    paddingVertical: 8,
    marginHorizontal: 5,
    borderRadius: 4,
    backgroundColor: '#f1f5f9',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  activeTabButton: { backgroundColor: '#fff', borderColor: '#0e4e96' },
  tabText: { color: '#64748B', fontWeight: '500' },
  activeTabText: { color: '#0e4e96', fontWeight: '600' },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  searchBar: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f1f5f9',
    borderRadius: 8,
    paddingHorizontal: 12,
    height: 40,
    marginRight: 10,
  },
  searchInput: { flex: 1, fontSize: 16, color: '#0f172a', marginLeft: 8 },
  filterButton: {
    backgroundColor: '#0e4e96',
    width: 40,
    height: 40,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  categoriesContainer: {
    paddingVertical: 10,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  categoryButton: {
    paddingHorizontal: 15,
    paddingVertical: 8,
    marginHorizontal: 5,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    backgroundColor: '#f1f5f9',
  },
  activeCategoryButton: { backgroundColor: '#fff', borderColor: '#0e4e96' },
  categoryText: { color: '#64748B' },
  activeCategoryText: { color: '#0e4e96', fontWeight: '600' },
  summaryContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  summaryItem: { alignItems: 'center' },
  summaryValue: { fontSize: 18, fontWeight: 'bold', color: '#0e4e96' },
  summaryLabel: { fontSize: 12, color: '#64748B', marginTop: 4 },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#f8fafc' },
  loadingText: { marginTop: 10, fontSize: 16, color: '#64748B' },
  supplierList: { padding: 12, backgroundColor: '#f8fafc' },
  supplierCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 10,
    elevation: 2,
  },
  inactiveCard: { opacity: 0.7 },
  supplierHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  supplierName: { fontSize: 18, fontWeight: '600', color: '#0f172a', flex: 1 },
  statusBadge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 4 },
  activeStatus: { backgroundColor: '#10B981' },
  inactiveStatus: { backgroundColor: '#64748B' },
  statusText: { color: '#fff', fontSize: 12, fontWeight: 'bold' },
  supplierMaterials: { fontSize: 14, color: '#64748B', marginBottom: 5 },
  supplierLocation: { fontSize: 14, color: '#64748B', marginBottom: 15 },
  ratingsContainer: { flexDirection: 'row', marginBottom: 15 },
  overallRating: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: '#f1f5f9',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 15,
  },
  overallRatingValue: { fontSize: 18, fontWeight: 'bold', color: '#0e4e96' },
  overallRatingLabel: { fontSize: 10, color: '#64748B' },
  detailedRatings: { flex: 1 },
  ratingItem: { flexDirection: 'row', alignItems: 'center', marginBottom: 5 },
  ratingLabel: { width: 70, fontSize: 12, color: '#64748B' },
  ratingBarContainer: { flex: 1, height: 6, backgroundColor: '#e2e8f0', borderRadius: 3, marginHorizontal: 10 },
  ratingBar: { height: '100%', backgroundColor: '#0e4e96', borderRadius: 3 },
  ratingValue: { width: 30, fontSize: 12, fontWeight: 'bold', color: '#0f172a', textAlign: 'right' },
  supplierActions: { flexDirection: 'row', justifyContent: 'space-between' },
  actionButton: {
    backgroundColor: '#0e4e96',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    marginHorizontal: 3,
  },
  primaryActionButton: { backgroundColor: '#fdb913' },
  actionButtonText: { color: '#fff', fontWeight: '600', fontSize: 12, marginLeft: 5 },
  emptyContainer: { alignItems: 'center', justifyContent: 'center', padding: 40, backgroundColor: '#f8fafc' },
  emptyTitle: { fontSize: 18, fontWeight: '600', color: '#0f172a', marginTop: 16, marginBottom: 8 },
  emptyText: { fontSize: 14, color: '#64748B', textAlign: 'center', marginBottom: 24 },
  addButtonEmptyState: { backgroundColor: '#0e4e96', paddingVertical: 12, paddingHorizontal: 20, borderRadius: 8 },
  addButtonText: { color: '#fff', fontSize: 16, fontWeight: '600' },
  floatingButton: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#0f172a',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 5,
    elevation: 5,
  },
  filterOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
    zIndex: 1000,
  },
  filterModal: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '80%',
  },
  filterHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 20, borderBottomWidth: 1, borderBottomColor: '#e2e8f0' },
  filterTitle: { fontSize: 18, fontWeight: 'bold', color: '#0f172a' },
  filterContent: { padding: 20 },
  filterSection: { marginBottom: 25 },
  filterSectionTitle: { fontSize: 16, fontWeight: 'bold', color: '#0f172a', marginBottom: 10 },
  filterOptions: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  filterOption: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    backgroundColor: '#f1f5f9',
  },
  activeFilterOption: { backgroundColor: '#fff', borderColor: '#0e4e96' },
  filterOptionText: { fontSize: 14, color: '#64748B' },
  activeFilterOptionText: { color: '#0e4e96', fontWeight: '600' },
  filterActions: { flexDirection: 'row', padding: 20, borderTopWidth: 1, borderTopColor: '#e2e8f0', gap: 10 },
  clearFiltersButton: { flex: 1, backgroundColor: 'transparent', borderWidth: 1, borderColor: '#0e4e96', paddingVertical: 12, borderRadius: 8, alignItems: 'center' },
  clearFiltersText: { color: '#0e4e96', fontWeight: '600' },
  applyFiltersButton: { flex: 1, backgroundColor: '#0e4e96', paddingVertical: 12, borderRadius: 8, alignItems: 'center' },
  applyFiltersText: { color: '#fff', fontWeight: '600' },
  detailOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  detailContainer: {
    backgroundColor: '#fff',
    borderRadius: 12,
    width: '90%',
    height: '70%',
    overflow: 'hidden',
  },
  detailHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 20, borderBottomWidth: 1, borderBottomColor: '#e2e8f0' },
  detailTitle: { fontSize: 20, fontWeight: '600', color: '#0f172a', flex: 1 },
  detailContent: { flex: 1, padding: 20 },
  detailSection: { marginBottom: 20 },
  sectionTitle: { fontSize: 16, fontWeight: '600', color: '#0f172a', marginBottom: 10 },
  contactItem: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  contactText: { fontSize: 14, color: '#64748B', marginLeft: 10 },
  materialTags: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  materialTag: { backgroundColor: '#f1f5f9', borderRadius: 4, paddingHorizontal: 12, paddingVertical: 6 },
  materialTagText: { fontSize: 12, color: '#64748B' },
  detailRatingsContainer: { flexDirection: 'row', alignItems: 'center' },
  detailOverallRating: { alignItems: 'center', marginRight: 20 },
  detailOverallValue: { fontSize: 32, fontWeight: 'bold', color: '#0e4e96' },
  detailOverallLabel: { fontSize: 12, color: '#64748B' },
  detailRatingsList: { flex: 1 },
  detailRatingItem: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  detailRatingLabel: { fontSize: 14, color: '#64748B', width: 80 },
  detailRatingBar: { flex: 1, marginHorizontal: 10 },
  detailRatingValue: { fontSize: 14, color: '#0f172a', width: 30, textAlign: 'right' },
  infoItem: { flexDirection: 'row', marginBottom: 8 },
  infoLabel: { fontSize: 14, fontWeight: '500', color: '#64748B', width: 120 },
  infoValue: { fontSize: 14, color: '#0f172a', flex: 1 },
  activeText: { color: '#10B981', fontWeight: '600' },
  inactiveText: { color: '#64748B', fontWeight: '600' },
  notesText: { fontSize: 14, color: '#64748B', lineHeight: 20 },
  detailActions: { flexDirection: 'row', gap: 10, marginTop: 20 },
  detailActionButton: { flex: 1, backgroundColor: '#0e4e96', borderRadius: 8, paddingVertical: 12, alignItems: 'center' },
  detailActionText: { color: '#fff', fontSize: 16, fontWeight: '600' },
  secondaryButton: { backgroundColor: '#fff', borderWidth: 1, borderColor: '#0e4e96' },
  secondaryButtonText: { color: '#0e4e96', fontSize: 16, fontWeight: '600' },
});

export default SuppliersVendorListScreen;